// [src/pages/Home/Home.jsx]

import React, { useState, useEffect, useCallback, useRef, useLayoutEffect } from 'react';
import { useNavigate } from "react-router-dom";
import { API_BASE_URL, TEST_API_BASE_URL } from "config/apiConfig";
import { getTokenStatus } from 'utils/tokenManager';
import { useAuthSession } from "hooks/useAuthSession";
import { formatTimeStr, formatTimeStr24 } from 'utils/formatters';
import { useBranch } from 'contexts/BranchContext';

// CSS imports
import 'styles/Font.css';

// Image imports
import crownIcon from 'assets/images/icon/crown-icon.svg';
import qrIcon from 'assets/images/icon/QR-icon.svg';
import defaultCoachImg from 'assets/images/coachSample/coach_ex1.svg';
import rightarrow from 'assets/images/icon/arrow-right-icon.svg';
import flagIcon from 'assets/images/icon/flag-icon.svg';
import starIcon from 'assets/images/icon/star-icon.svg';
import dotIcon from 'assets/images/icon/dot-icon.svg';

// Components imports
import QrModal from 'components/ui/Modals/QrModal/QrModal';
import BottomSheet from 'components/ui/BottomSheet/BottomSheet';
import Footer from "components/layout/Footer/Footer";
import AppHeader from 'components/layout/AppHeader/AppHeader';
import LocationBar from 'components/layout/LocationBar/LocationBar';
import NotYetModal from "components/ui/Modals/NotYetModal/NotYetModal";
import ConfirmModal from "components/ui/Modals/ConfirmModal/ConfirmModal";
import usePreventHorizontalScroll from "hooks/usePreventHorizontalScroll";
import usePreventInputScroll from "hooks/usePreventInputScroll";

// --- [Sub Component] 부스트 코치 카드 ---
const BoostCoachCard = React.memo(({
    coach,
    index,
    boostTime,
    inactiveTimeIds,
    onTimeClick,
    onViewAll,
    onCoachClick
}) => {
    const coachId = coach.id || coach.sbKey || index;
    const timeList = (coach.times && coach.times.length > 0) ? coach.times : (boostTime && boostTime.length > 0 ? boostTime : []);

    // 시간이 지났는지 판별하는 함수
    const checkIsPast = (timeData) => {
        const now = new Date();
        let targetDateStr = '';
        let targetTimeStr = '';

        if (typeof timeData === 'object' && timeData !== null) {
            targetDateStr = timeData.stdate;
            targetTimeStr = timeData.stStime;
        } else {
            const yyyy = now.getFullYear();
            const mm = String(now.getMonth() + 1).padStart(2, '0');
            const dd = String(now.getDate()).padStart(2, '0');
            targetDateStr = `${yyyy}-${mm}-${dd}`;

            const label = formatTimeStr24(timeData);
            targetTimeStr = label;
        }

        if (!targetDateStr || !targetTimeStr) return false;

        // "YYYY-MM-DDTHH:mm" 포맷으로 변환
        const targetDate = new Date(`${targetDateStr}T${targetTimeStr}:00`);
        return now > targetDate;
    };

    // [핵심 수정] 렌더링하기 전에 '미래 시간'만 남기고 필터링 (지난 시간 숨김 처리)
    const visibleTimeList = timeList.filter(timeData => !checkIsPast(timeData));

    return (
        <div style={staticStyles.coachCard}>
            <div
                style={{ ...staticStyles.coachCardImageContainer, cursor: 'pointer' }}
                onClick={() => onCoachClick(coach)}
            >
                <img
                    src={coach.sbImgUrl || coach.img || defaultCoachImg}
                    alt="코치사진"
                    style={staticStyles.coachCardImage}
                />
                <img src={flagIcon} alt="찜하기" style={staticStyles.coachCardFlagIcon} />
            </div>

            <div style={staticStyles.coachCardInfo}>
                <span
                    style={{ ...staticStyles.coachCardName, cursor: 'pointer' }}
                    onClick={() => onCoachClick(coach)}
                >
                    {coach.sbName || coach.coachName || coach.exerciseName || '트레이너 이름'}
                </span>

                <div style={staticStyles.coachCardStats}>
                    <img src={starIcon} alt="별점" style={staticStyles.coachCardStarIcon} />
                    <span>{coach.sbRating ? Number(coach.sbRating).toFixed(1) : '0.0'}</span>
                    <img src={dotIcon} alt="dot" />
                    <span>리뷰 {coach.sbReviewCnt || coach.reviews || 0}개</span>
                </div>

                <div style={staticStyles.coachCardTimeContainer}>
                    {visibleTimeList.length === 0 ? (
                        <span style={{ fontSize: '12px', color: '#747474', padding: '5px' }}>
                            예약 가능한 시간이 없습니다.
                        </span>
                    ) : (
                        // 필터링된 목록(visibleTimeList)만 렌더링
                        visibleTimeList.map((timeData, tIndex) => {
                            const isObject = typeof timeData === 'object' && timeData !== null;
                            let timeLabel = isObject ? (formatTimeStr24(timeData.stStime) || "시간") : formatTimeStr24(timeData);
                            let displayLabel = timeLabel;

                            if (isObject) {
                                const total = timeData.stCnt || timeData.totalCnt;
                                const rem = timeData.remCnt;
                                // 데이터가 모두 유효할 때만 표시 (0 포함)
                                if (total !== undefined && rem !== undefined) {
                                    displayLabel = `${timeLabel} (${total - rem}/${total})`;
                                }
                            }

                            const uniqueKey = isObject ? timeData.stKey : `${tIndex}`;
                            const buttonKey = `coach-${coachId}-${uniqueKey}`;

                            const isInactive = inactiveTimeIds.has(buttonKey);

                            // 날짜 정보 추출 (stdate가 있으면 사용, 없으면 오늘 날짜)
                            let targetDateStr = '';
                            if (isObject && timeData.stdate) {
                                targetDateStr = timeData.stdate;
                            } else {
                                const now = new Date();
                                const yyyy = now.getFullYear();
                                const mm = String(now.getMonth() + 1).padStart(2, '0');
                                const dd = String(now.getDate()).padStart(2, '0');
                                targetDateStr = `${yyyy}-${mm}-${dd}`;
                            }

                            return (
                                <button
                                    key={buttonKey}
                                    style={isInactive ? staticStyles.coachCardTimeButtonInactive : staticStyles.coachCardTimeButtonActive}
                                    onClick={() => onTimeClick(buttonKey, (coach.sbName || coach.coachName), timeLabel, (coach.sbImgUrl || coach.img), targetDateStr)}
                                >
                                    {displayLabel}
                                </button>
                            );
                        })
                    )}
                </div>
            </div>
        </div>
    );
});

// --- [Sub Component] 회원정보 수정 모달 ---
const ProfileUpdateModal = ({ isOpen, onClose, onConfirm }) => {
    const [formData, setFormData] = useState({
        userName: '',
        password: '',
        passwordCheck: ''
    });

    if (!isOpen) return null;

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = () => {
        if (!formData.userName || !formData.password) {
            alert("이름과 비밀번호를 모두 입력해주세요.");
            return;
        }
        if (formData.password !== formData.passwordCheck) {
            alert("비밀번호가 일치하지 않습니다.");
            return;
        }
        onConfirm(formData.userName, formData.password);
    };

    return (
        <div style={staticStyles.modalOverlay}>
            <div style={staticStyles.modalContent}>
                <h3 style={staticStyles.modalTitle}>회원 정보 수정</h3>
                <p style={staticStyles.modalDesc}>
                    키오스크 간편 가입 회원입니다.<br />
                    원활한 앱 사용을 위해 정보를 수정해주세요.
                </p>

                <div style={staticStyles.inputGroup}>
                    <input
                        style={staticStyles.modalInput}
                        name="userName"
                        placeholder="이름"
                        value={formData.userName}
                        onChange={handleChange}
                    />
                    <input
                        style={staticStyles.modalInput}
                        name="password"
                        type="password"
                        placeholder="새 비밀번호 (숫자 4자리)"
                        maxLength={4} // 키오스크 회원이면 보통 간편 비번 사용, 정책에 따라 변경
                        value={formData.password}
                        onChange={handleChange}
                    />
                    <input
                        style={staticStyles.modalInput}
                        name="passwordCheck"
                        type="password"
                        placeholder="비밀번호 확인"
                        maxLength={4}
                        value={formData.passwordCheck}
                        onChange={handleChange}
                    />
                </div>

                <div style={staticStyles.modalButtonData}>
                    <button style={staticStyles.modalConfirmButton} onClick={handleSubmit}>
                        수정 완료
                    </button>
                    {/* 강제성이 필요하면 닫기 버튼 제거 */}
                    <button style={staticStyles.modalCloseButton} onClick={onClose}>
                        나중에 하기
                    </button>
                </div>
            </div>
        </div>
    );
};

function Home() {
    usePreventInputScroll();
    usePreventHorizontalScroll();

    const navigate = useNavigate();
    const { currentBranch, selectBranch } = useBranch();

    // --- State ---
    const { userInfo, loading: authLoading } = useAuthSession({ requireAuth: true });
    const [slickStatus, setSlickStatus] = useState('N');
    const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
    const [alarms, setAlarms] = useState(null);
    const [showQR, setShowQR] = useState(false);
    const [showSheet, setShowSheet] = useState(false);
    const [selectedCoachInfo, setSelectedCoachInfo] = useState(null);
    const [inactiveTimeIds, setInactiveTimeIds] = useState(new Set());
    const [isNotYetModalOpen, setIsNotYetModalOpen] = useState(false);
    const [notYetMessage, setNotYetMessage] = useState("");
    const [qrData, setQrData] = useState(null);
    const [isQrLoading, setIsQrLoading] = useState(false);
    const [initialTimeData, setInitialTimeData] = useState(null);
    const [boostCoaches, setBoostCoaches] = useState([]);
    const [boostTime, setBoostTime] = useState([]);
    const [iptCoaches, setIptCoaches] = useState([]);
    const [lyloCoaches, setLyloCoaches] = useState([]);
    const [otCoaches, setOtCoaches] = useState([]);
    const [ptCoaches, setPtCoaches] = useState([]);
    const [availableStKinds, setAvailableStKinds] = useState([]);
    const [paymentStatus, setPaymentStatus] = useState(() => {
        return sessionStorage.getItem('paymentStatus') || localStorage.getItem('paymentStatus') || 'N';
    });
    // const [isAlarmOpen, setIsAlarmOpen] = useState(false);
    const scrollRef = useRef(null);
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [pendingReservation, setPendingReservation] = useState(null);

    // [!! 추가: 동적 높이 측정을 위한 ref와 state !!]
    const headerRef = useRef(null);
    const [headerHeight, setHeaderHeight] = useState(60);

    // 바텀 시트에 표시할 시간 목록 상태
    const [sheetTimes, setSheetTimes] = useState([]);
    const [currentSelectedDate, setCurrentSelectedDate] = useState("");

    // 공통 알림 모달 상태
    const [alertModal, setAlertModal] = useState({
        isOpen: false,
        title: '',
        message: ''
    });

    // 컴포넌트 마운트 시 스크롤을 강제로 맨 위로 초기화
    useLayoutEffect(() => {
        // 브라우저의 자동 스크롤 복원 기능 비활성화 (가장 중요)
        if ('scrollRestoration' in window.history) {
            window.history.scrollRestoration = 'manual';
        }

        // 즉시 스크롤 0으로 초기화
        if (scrollRef.current) {
            scrollRef.current.scrollTop = 0;
        }

        // 약간의 지연 후 재확인 (Scroll Snap이나 렌더링 지연으로 인한 튀는 현상 방지)
        const timer = setTimeout(() => {
            if (scrollRef.current) {
                scrollRef.current.scrollTop = 0;
            }
        }, 100);

        // 컴포넌트 언마운트 시(다른 페이지 이동 시) 스크롤 복원 설정을 기본값(auto)으로 돌려놓을지 결정
        return () => {
            clearTimeout(timer);
            // 페이지를 나갈 때는 다시 자동 복원으로 돌려놓음 (선택 사항)
            if ('scrollRestoration' in window.history) {
                window.history.scrollRestoration = 'auto';
            }
        };
    }, []);

    // [2] useEffect 추가: 유저 정보 로드 시 이름 체크
    useEffect(() => {
        if (userInfo && userInfo.userName === '키오스크가입') {
            setIsProfileModalOpen(true);
        }
    }, [userInfo]);

    // 정보 수정 핸들러 (FormData 사용 및 헤더 수정)
    const handleProfileUpdateConfirm = useCallback(async (newName, newPassword) => {
        const { token } = getTokenStatus();
        if (!token) return;

        try {
            const UPDATE_API_URL = `${API_BASE_URL}/kioskUser`;

            // [핵심 변경 1] URLSearchParams 대신 FormData 사용
            // MyProfileEdit.jsx와 동일한 방식으로 데이터 구성
            const formData = new FormData();
            formData.append('userName', newName);
            formData.append('password', newPassword);

            const response = await fetch(UPDATE_API_URL, {
                method: 'POST',
                headers: {
                    // [핵심 변경 2] FormData 전송 시 Content-Type 헤더를 삭제
                    // 브라우저가 자동으로 multipart/form-data 및 boundary를 설정합니다.
                    'Authorization': `Bearer ${token}`
                },
                body: formData
            });

            const resData = await response.json();
            const data = resData.data || resData;

            if (response.ok && (data.code === 200 || data.code === '200' || data.status === 'success')) {
                alert("정보가 수정되었습니다.\n원활한 이용을 위해 앱을 새로고침합니다.");
                setIsProfileModalOpen(false);
                window.location.reload();
            } else {
                alert(data.message || "정보 수정에 실패했습니다.");
            }
        } catch (e) {
            console.error("Profile Update Error:", e);
            alert("서버 통신 오류가 발생했습니다.");
        }
    }, [userInfo]);

    // 날짜 변경 핸들러 (API 호출)
    const handleDateChange = useCallback(async (date) => {
        setCurrentSelectedDate(date); // 선택된 날짜 상태 업데이트
        if (!selectedCoachInfo) return;
        const { token } = getTokenStatus();
        const allCoaches = [...boostCoaches, ...iptCoaches, ...lyloCoaches];
        const targetCoach = allCoaches.find(c => (c.sbName || c.coachName || c.exerciseName) === selectedCoachInfo.coachName);
        const mnKey = targetCoach ? (targetCoach.ctKey || targetCoach.sbKey || targetCoach.id) : null;
        const stKind = targetCoach ? (targetCoach.stKind || 'E') : 'E';
        const brKey = currentBranch.brKey;

        if (!token || !mnKey) return;

        try {
            const SCHEDULE_API_URL = `${API_BASE_URL}/schedule?brKey=${brKey}&mnKey=${mnKey}&stKind=${stKind}&date=${date}`;
            const response = await fetch(SCHEDULE_API_URL, {
                method: 'GET',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            const resData = await response.json();
            const data = resData.data || resData;

            let times = [];
            if (data.list && Array.isArray(data.list)) {
                times = data.list;
            } else if (data.times && Array.isArray(data.times)) {
                times = data.times;
            }
            setSheetTimes(times);
        } catch (e) {
            console.error("Schedule Fetch Error:", e);
            setSheetTimes([]); // 에러 시 빈 목록
        }
    }, [selectedCoachInfo, boostCoaches, currentBranch]); // currentBranch 의존성 추가

    // --- [API] 대시보드 데이터 로드 ---
    const loadDashboardData = useCallback(async () => {
        const { token } = getTokenStatus();
        if (!token) return;

        const payload = {
            brKey: currentBranch.brKey,
        }

        try {
            // const response = await fetch(`${API_BASE_URL}/home`, {
            const response = await fetch(`${API_BASE_URL}/home`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                    'Authorization': `Bearer ${token}`
                },
                body: new URLSearchParams(payload)
            });

            if (!response.ok) return;

            const resData = await response.json();
            const data = resData.data || resData;

            // 서버에서 내려준 지점 정보가 있고, 현재 상태와 다르거나 푸터 정보가 비어있다면 전역 상태 업데이트
            const serverFooter = data.footerInfo || {};
            const currentFooter = currentBranch.footerInfo || {};

            const isBranchDifferent = data.brKey && (data.brKey !== currentBranch.brKey || data.brName !== currentBranch.brName);
            const isFooterEmpty = !currentFooter.companyName && (serverFooter.companyName || data.brCompanyName);

            // [추가] 지원되는 stKind 정보 파싱
            let parsedStKinds = ['E'];
            if (data.stKind) {
                parsedStKinds = data.stKind.split(',').map(s => s.trim());
            }
            setAvailableStKinds(parsedStKinds); // 로컬 상태도 유지 (Home.jsx 내부 필터링 용)

            // 전역 상태의 availableStKinds와 다르면 업데이트 플래그
            const currentStKinds = currentBranch.availableStKinds || [];
            const isStKindsDifferent = parsedStKinds.length !== currentStKinds.length ||
                !parsedStKinds.every(k => currentStKinds.includes(k));

            if (isBranchDifferent || isFooterEmpty || isStKindsDifferent) {
                const newFooterInfo = data.footerInfo ? {
                    companyName: data.footerInfo.companyName || '',
                    ceoName: data.footerInfo.ceoName || '',
                    businessNumber: data.footerInfo.businessNumber || '',
                    ecommerceNumber: data.footerInfo.ecommerceNumber || '',
                    address: data.footerInfo.address || '',
                    csPhone: data.footerInfo.csPhone || '',
                    email: data.footerInfo.email || ''
                } : {
                    companyName: data.brCompanyName || '',
                    ceoName: data.brCeoName || '',
                    businessNumber: data.brBizNo || '',
                    ecommerceNumber: data.brMailOrderNo || '',
                    address: data.brAddr || '',
                    csPhone: data.brTel || '',
                    email: data.brEmail || ''
                };

                selectBranch({
                    brKey: data.brKey || currentBranch.brKey,
                    brName: data.brName || currentBranch.brName,
                    isProductAvailable: data.brKey === '2' ? 'Y' : (data.isProductAvailable || currentBranch.isProductAvailable || 'N'),
                    footerInfo: newFooterInfo,
                    availableStKinds: parsedStKinds
                });
            }

            // [2. API 데이터 저장] slickStatus 값 저장
            if (data.slickStatus) {
                setSlickStatus(data.slickStatus);
            }

            if (data.paymentStatus) {
                setPaymentStatus(data.paymentStatus);
                sessionStorage.setItem('paymentStatus', data.paymentStatus);
            }

            // [3. 수업 정보 파싱 헬퍼 함수]
            const processClassInfo = (info) => {
                if (!info) return { coaches: [], times: [] };
                let rawList = [];
                let globalTimes = [];

                if (Array.isArray(info)) {
                    rawList = info;
                } else if (typeof info === 'object' && info !== null) {
                    rawList = info.list || Object.values(info);
                }

                if (rawList.length > 0 && rawList[0].times) {
                    globalTimes = rawList[0].times;
                }

                // 기본 유효성 검사
                let validCoaches = rawList.filter(coach => coach && (coach.coachName || coach.sbName || coach.exerciseName));

                const now = new Date();
                const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

                // 금주 월~토 날짜 범위 계산
                const getThisWeekRange = () => {
                    const d = new Date();
                    const day = d.getDay();
                    const diffToMonday = day === 0 ? -6 : 1 - day;
                    const start = new Date(d);
                    start.setDate(d.getDate() + diffToMonday);
                    start.setHours(0, 0, 0, 0);

                    const end = new Date(start);
                    end.setDate(start.getDate() + 5);
                    end.setHours(23, 59, 59, 999);
                    return { start, end };
                };
                const { start: weekStart, end: weekEnd } = getThisWeekRange();

                // 금주 월~토 수업만 남기고, 남은 수업이 없는 코치는 제외
                validCoaches = validCoaches.map(coach => {
                    const times = coach.times || globalTimes;
                    if (!times) return coach;

                    const filteredTimes = times.filter(t => {
                        const targetDateStr = (typeof t === 'object' && t !== null) ? t.stdate : todayStr;
                        if (!targetDateStr) return false;
                        const timeDate = new Date(`${targetDateStr}T00:00:00`);
                        return timeDate >= weekStart && timeDate <= weekEnd;
                    });
                    return { ...coach, times: filteredTimes };
                }).filter(coach => coach.times && coach.times.length > 0);

                const getEarliestFutureTimestamp = (coach) => {
                    const timeList = (coach.times && coach.times.length > 0) ? coach.times : globalTimes;
                    let earliest = Number.MAX_SAFE_INTEGER;
                    for (const t of timeList) {
                        let targetDateStr = (typeof t === 'object' && t !== null) ? t.stdate : todayStr;
                        let targetTimeStr = (typeof t === 'object' && t !== null) ? t.stStime : formatTimeStr(t);
                        if (targetDateStr && targetTimeStr) {
                            const timeDate = new Date(`${targetDateStr}T${targetTimeStr}:00`);
                            const timeValue = timeDate.getTime();
                            if (timeValue > now.getTime() && timeValue < earliest) earliest = timeValue;
                        }
                    }
                    return earliest;
                };

                validCoaches.sort((a, b) => getEarliestFutureTimestamp(a) - getEarliestFutureTimestamp(b));
                return { coaches: validCoaches, times: globalTimes };
            };

            // 데이터 초기화 및 분할 로직
            let finalBoost = [];
            let finalIpt = [];
            let finalLylo = [];
            let finalBoostTime = [];

            if (data.sbInfo) {
                const { coaches, times } = processClassInfo(data.sbInfo);
                finalBoostTime = times;

                // stKind별로 분리 (기본값 'E')
                coaches.forEach(c => {
                    const kind = c.stKind || 'E';
                    if (kind === 'E') finalBoost.push(c);
                    else if (kind === 'G') finalIpt.push(c);
                    else if (kind === 'F') finalLylo.push(c);
                });
            }

            if (data.iptInfo) {
                const { coaches } = processClassInfo(data.iptInfo);
                // 중복 방지 로직이 필요할 수 있으나, 일단 합침
                finalIpt = [...finalIpt, ...coaches];
            }

            if (data.raInfo) {
                const { coaches } = processClassInfo(data.raInfo);
                finalLylo = [...finalLylo, ...coaches];
            }

            setBoostCoaches(finalBoost);
            setIptCoaches(finalIpt);
            setLyloCoaches(finalLylo);
            setBoostTime(finalBoostTime);

            if (data.otInfo) {
                let otList = [];
                if (Array.isArray(data.otInfo)) {
                    otList = data.otInfo;
                } else if (typeof data.otInfo === 'object' && data.otInfo !== null) {
                    otList = data.otInfo.list || Object.values(data.otInfo);
                }
                setOtCoaches(otList);
            }

            if (data.ptInfo) {
                let ptList = [];
                if (Array.isArray(data.ptInfo)) {
                    ptList = data.ptInfo;
                } else if (typeof data.ptInfo === 'object' && data.ptInfo !== null) {
                    ptList = data.ptInfo.list || Object.values(data.ptInfo);
                }
                setPtCoaches(ptList);
            }

        } catch (e) {
            console.error("Dashboard Load Error:", e);
            if (e.message.includes('401')) {
                navigate('/', { replace: true });
            }
        }
    }, [navigate, currentBranch, selectBranch]);

    // 인증 완료 시 데이터 로드
    useEffect(() => {
        if (!authLoading && userInfo) {
            loadDashboardData();
        }
    }, [authLoading, userInfo, loadDashboardData]);

    // --- Handlers ---
    // 슬릭부스트 수업 클릭 핸들러 (stKind: 'E')
    const handleBoostCoachClick = useCallback((coach) => {
        const ctKey = coach.ctKey || coach.sbKey || coach.id;
        const dateStr = coach.times && coach.times.length > 0 ? coach.times[0].stdate : undefined;
        navigate('/sb/detail', {
            state: {
                ctKey: ctKey,
                stKind: 'E',
                brKey: currentBranch.brKey,
                exercise: {
                    exerciseName: coach.sbName || coach.coachName || coach.exerciseName,
                    intro: coach.cont || coach.intro,
                    img: coach.img
                },
                date: dateStr
            }
        });
    }, [navigate, currentBranch]);

    // IPT 수업 클릭 핸들러 (stKind: 'G')
    const handleIptCoachClick = useCallback((coach) => {
        const ctKey = coach.ctKey || coach.sbKey || coach.id;
        const dateStr = coach.times && coach.times.length > 0 ? coach.times[0].stdate : undefined;
        navigate('/ipt/detail', {
            state: {
                ctKey: ctKey,
                stKind: 'G',
                brKey: currentBranch.brKey,
                exercise: {
                    exerciseName: coach.sbName || coach.coachName || coach.exerciseName,
                    intro: coach.cont || coach.intro,
                    img: coach.img
                },
                date: dateStr
            }
        });
    }, [navigate, currentBranch]);

    // LYLO 수업 클릭 핸들러 (stKind: 'F')
    const handleLyloCoachClick = useCallback((coach) => {
        const ctKey = coach.ctKey || coach.sbKey || coach.id;
        const dateStr = coach.times && coach.times.length > 0 ? coach.times[0].stdate : undefined;
        navigate('/lylo/detail', {
            state: {
                ctKey: ctKey,
                stKind: 'F',
                brKey: currentBranch.brKey,
                exercise: {
                    exerciseName: coach.sbName || coach.coachName || coach.exerciseName,
                    intro: coach.cont || coach.intro,
                    img: coach.img
                },
                date: dateStr
            }
        });
    }, [navigate, currentBranch]);

    // PT 트레이너 클릭 핸들러 (stKind: 'C')
    const handlePtCoachClick = useCallback((trainer) => {
        const mnKey = trainer.mnKey || trainer.id;
        const targetDate = currentSelectedDate || (trainer.times && trainer.times[0] && trainer.times[0].stdate) || "";
        navigate('/pt/detail', {
            state: {
                mnKey: mnKey,
                stKind: 'C',
                brKey: currentBranch.brKey,
                trainer: trainer,
                date: targetDate
            }
        });
    }, [navigate, currentBranch, currentSelectedDate]);

    const handleSleekBoostClick = useCallback((e) => {
        if (e) e.stopPropagation();
        navigate('/sb', {
            state: {
                brKey: currentBranch.brKey,
                stKind: 'E',
                brName: currentBranch.brName
            }
        });
    }, [navigate, currentBranch]);

    const handleIptViewAllClick = useCallback((e) => {
        if (e) e.stopPropagation();
        navigate('/ipt', {
            state: {
                brKey: currentBranch.brKey,
                stKind: 'G',
                brName: currentBranch.brName
            }
        });
    }, [navigate, currentBranch]);

    const handleLyloViewAllClick = useCallback((e) => {
        if (e) e.stopPropagation();
        navigate('/lylo', {
            state: {
                brKey: currentBranch.brKey,
                stKind: 'F',
                brName: currentBranch.brName
            }
        });
    }, [navigate, currentBranch]);

    const handlePtViewAllClick = useCallback((e) => {
        if (e) e.stopPropagation();
        navigate('/pt', {
            state: {
                brKey: currentBranch.brKey,
                stKind: 'C',
                brName: currentBranch.brName
            }
        });
    }, [navigate, currentBranch]);

    // 바텀시트 오픈 시 해당 코치의 시간표 데이터를 sheetTimes에 주입
    // TODO: 수업 종류 별 Status값을 각각 가져오도록 할지 논의 필요(2026.05.21)
    const handleTimeButtonClick = useCallback((id, coachName, time, img, dateStr) => {
        // 무료 OT 예약의 경우 slickStatus 검증 대상에서 제외함
        const isOtClass = otCoaches.some(c => (c.sbName || c.coachName || c.exerciseName) === coachName);

        // [추가된 로직] slickStatus가 'Y'가 아니면 예약 모달을 띄우지 않고 경고창 표시 (무료 OT 예약은 예외)
        if (!isOtClass && slickStatus !== 'Y') {
            setAlertModal({
                isOpen: true,
                title: '예약 불가',
                message: `[${currentBranch?.brName}] 수업 예약 권한이 없습니다.\n(이용권을 구매해주세요)`
            });
            return;
        }

        // 날짜 동기화
        if (dateStr) {
            setCurrentSelectedDate(dateStr);
        }

        // 시간 포맷 변환 (예: "14:00" -> "오후 2:00")
        if (time) {
            // "14:00" 형식이면 변환, 이미 "오후 2:00" 형식이면 그대로 사용
            if (time.includes(':') && !time.includes('오')) {
                const parts = time.split(':');
                let hour = parseInt(parts[0], 10);
                const minute = parts[1];
                const period = hour >= 12 ? '오후' : '오전';
                let displayHour = hour > 12 ? hour - 12 : hour;
                if (displayHour === 0) displayHour = 12;
                const formattedTime = `${period} ${displayHour}:${minute}`;
                setInitialTimeData({ coachName, time: formattedTime, img });
            } else {
                setInitialTimeData({ coachName, time, img });
            }
        } else {
            setInitialTimeData(null);
        }

        // 선택된 코치 찾기 (모든 수업 타입에서 검색)
        const allCoaches = [...boostCoaches, ...iptCoaches, ...lyloCoaches, ...ptCoaches, ...otCoaches];
        const targetCoach = allCoaches.find(c => (c.sbName || c.coachName || c.exerciseName) === coachName);

        // 해당 코치의 시간표를 바텀시트용 state에 설정
        if (targetCoach) {
            const initialTimes = (targetCoach.times && targetCoach.times.length > 0)
                ? targetCoach.times
                : boostTime;
            setSheetTimes(initialTimes);
        } else {
            setSheetTimes([]);
        }

        setShowSheet(true);
    }, [boostCoaches, iptCoaches, lyloCoaches, ptCoaches, otCoaches, boostTime, slickStatus]); // [의존성 추가] slickStatus 추가

    const handleCloseNotYetModal = useCallback(() => {
        setIsNotYetModalOpen(false);
        setNotYetMessage("");
    }, []);
    const handleViewAllClick = useCallback((e) => {
        e?.preventDefault();
        setNotYetMessage(`해당 서비스는 준비 중입니다`);
        setIsNotYetModalOpen(true);
    }, []);
    const handleMembershipClick = useCallback((e) => {
        e.stopPropagation();
        if (currentBranch && currentBranch.brKey) {
            navigate(`/prod/${currentBranch.brKey}`);
        } else {
            navigate('/branch');
        }
    }, [navigate, currentBranch]);
    const closeQrModal = useCallback(() => {
        setShowQR(false);
        setQrData(null);
    }, []);
    const handleCloseSheet = useCallback(() => setShowSheet(false), []);
    // const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
    const handleLocationSelectClick = useCallback(() => {
        navigate('/branch');
    }, [navigate]);

    const handleSheetConfirm = useCallback((data) => {
        setPendingReservation(data);
        setIsConfirmModalOpen(true);
    }, []);

    // 예약 확정 핸들러 (변수명 date로 수정됨)
    const handleFinalReservation = useCallback(async () => {
        // 데이터 검증
        if (!pendingReservation) {
            setAlertModal({ isOpen: true, title: '오류', message: '예약 정보가 없습니다.' });
            return;
        }

        // [수정 포인트] 여기서 stdate가 아니라 'date'를 가져와야 합니다.
        const { stKey, date, time } = pendingReservation;

        if (!stKey) {
            setAlertModal({ isOpen: true, title: '오류', message: '유효하지 않은 시간대입니다.\n(Schedule Key Missing)' });
            return;
        }

        const { token } = getTokenStatus();
        const brKey = currentBranch.brKey || '1';

        // 코치 이름으로 stKind 찾기
        const allCoaches = [...boostCoaches, ...iptCoaches, ...lyloCoaches];
        const targetCoach = allCoaches.find(c => (c.sbName || c.coachName || c.exerciseName) === pendingReservation.coachName);
        const stKind = targetCoach ? (targetCoach.stKind || 'E') : 'E';

        try {
            // API 호출
            // const response = await fetch(`${API_BASE_URL}/regSchedule`, {
            const response = await fetch(`${API_BASE_URL}/regSchedule`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                    'Authorization': `Bearer ${token}`
                },
                body: new URLSearchParams({
                    brKey: brKey,
                    stKey: stKey,
                    stKind: stKind,
                })
            });

            const data = await response.json();
            const resData = data.data || data;

            const code = resData.code || resData.data?.code;
            const status = resData.status;

            // 성공 여부 판단
            if (status === 'success' || code === 200 || code === '0000') {
                setAlertModal({
                    isOpen: true,
                    title: '예약 확정',
                    // [수정 포인트] 변수명 date 사용
                    message: `[${date} ${time}]\n예약이 완료되었습니다.`,
                    onConfirm: () => {
                        loadDashboardData();
                        closeAlertModal();
                    }
                });

            } else {
                const errorMsg = resData.message || resData.data?.message || '예약에 실패했습니다.';
                setAlertModal({ isOpen: true, title: '예약 실패', message: errorMsg });
            }

        } catch (e) {
            console.error("Reservation Error:", e);
            setAlertModal({ isOpen: true, title: '통신 오류', message: "서버와 연결할 수 없습니다." });
        } finally {
            setIsConfirmModalOpen(false);
            setPendingReservation(null);
        }
    }, [pendingReservation, currentBranch, loadDashboardData]);

    // 모달 닫기 핸들러들
    const handleCloseConfirmModal = () => {
        setIsConfirmModalOpen(false);
        setPendingReservation(null);
    };

    const handleQrClick = useCallback(async () => {
        const API_URL = `${API_BASE_URL}/qr`;
        const { token } = getTokenStatus();
        if (showSheet) handleCloseSheet();
        if (isQrLoading) return;
        setIsQrLoading(true);
        setQrData(null);
        try {
            if (!token) throw new Error("로그인이 필요합니다.");
            const response = await fetch(API_URL, { method: 'POST', headers: { 'Authorization': `Bearer ${token}` } });
            const data = await response.json();
            const resData = data.data || data;

            // code가 200이 아닌 경우 처리
            const code = resData.code || resData.data?.code;
            if (code && code !== 200 && code !== '200' && code !== '0000') {
                const errorMsg = resData.message || resData.data?.message || "QR 코드를 불러올 수 없습니다.";
                setAlertModal({
                    isOpen: true,
                    title: '알림',
                    message: errorMsg
                });
                return;
            }

            if (resData.success === false) throw new Error("QR 생성 실패");
            if (resData.qr) {
                setQrData(resData.qr);
                setShowQR(true);
            }
        } catch (error) {
            console.error("QR Error:", error);
            // QR 에러 Alert 모달
            setAlertModal({
                isOpen: true,
                title: '알림',
                message: error.message || "QR 코드를 불러올 수 없습니다."
            });
        } finally {
            setIsQrLoading(false);
        }
    }, [isQrLoading, showSheet, handleCloseSheet]);

    // 모달 닫기
    const closeAlertModal = () => {
        setAlertModal(prev => ({ ...prev, isOpen: false }));
    };

    /*==============================동적 높이 측정===============================*/
    useEffect(() => {
        const measureHeight = () => {
            if (headerRef.current) {
                setHeaderHeight(headerRef.current.offsetHeight);
            }
        };

        // 마운트 직후 측정
        measureHeight();

        // 혹시 모를 레이아웃 시프트를 대비해 약간의 지연 후 재측정 (선택 사항)
        setTimeout(measureHeight, 100);

        let animationFrame;
        const debouncedMeasure = () => {
            if (animationFrame) cancelAnimationFrame(animationFrame);
            animationFrame = requestAnimationFrame(measureHeight);
        };

        window.addEventListener('resize', debouncedMeasure);
        return () => {
            window.removeEventListener('resize', debouncedMeasure);
            if (animationFrame) cancelAnimationFrame(animationFrame);
        };
    }, []);
    /*======================================================================*/

    // --- Render ---
    if (authLoading) {
        return <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100dvh',
            background: '#0D042D',
            color: 'white'
        }}><p>로딩 중...</p></div>;
    }

    /*==============================동적 높이 측정===============================*/
    const dynamicMainContentStyle = {
        ...staticStyles.mainContent,
        // paddingTop: `${headerHeight}px`,
        paddingTop: '0',
        paddingBottom: '100px',
        paddingLeft: '24px',
        paddingRight: '24px'
    };
    /*======================================================================*/

    return (
        <div ref={scrollRef} style={staticStyles.homeBackgroundWrapper}>
            <div style={staticStyles.fixedHeaderWrapper} ref={headerRef}>
                <div className="statusBar" style={staticStyles.statusBar}></div>
                <AppHeader
                    type="home"
                    alarms={alarms}
                />
            </div>

            <main style={dynamicMainContentStyle}>
                <div style={staticStyles.locationBarWrapper}>
                    <LocationBar
                        // Context 값을 바로 표시
                        branchName={currentBranch.brName}
                        onLocationSelectClick={handleLocationSelectClick}
                    // ...
                    />
                </div>

                <div style={staticStyles.userCardWrapper}>
                    <div
                        style={paymentStatus === 'N' ? staticStyles.userCardInnerBasic : staticStyles.userCardInnerPremium}>
                        <div style={staticStyles.userCardHeader}>
                            <span style={staticStyles.userCardName}>
                                {userInfo ? `안녕하세요, ${userInfo.userName}님` : '안녕하세요, 게스트님'}
                            </span>
                        </div>
                        <p style={staticStyles.userCardText}>
                            {paymentStatus === 'Y' ? '다양한 멤버십 혜택을 즐겨보세요' : '회원권으로 더 많은 혜택을 누려보세요'}
                        </p>
                        {paymentStatus === 'Y' ? (
                            currentBranch.brKey === '2' && (
                                <button onClick={handleQrClick} style={staticStyles.userCardQrButton}
                                    disabled={isQrLoading}>
                                    <span
                                        style={staticStyles.userCardQrButtonText}>{isQrLoading ? 'QR 생성...' : 'QR 체크인하기'}</span>
                                    <img src={qrIcon} alt="QR" style={staticStyles.userCardQrIcon} />
                                </button>
                            )
                        ) : (
                            currentBranch.isProductAvailable === 'Y' && (
                                <button onClick={handleMembershipClick} style={staticStyles.userCardMembershipButton}>
                                    <span style={staticStyles.userCardMembershipButtonText}>멤버십 구독하기</span>
                                    <img src={crownIcon} alt="Crown" style={staticStyles.userCardCrownIcon} />
                                </button>
                            )
                        )}
                    </div>
                </div>

                {/* 슬릭부스트 섹션 */}
                {availableStKinds.includes('E') && (
                    <div className='boost-section' style={staticStyles.boostSection}>
                        <div className='boost-section-nav' style={staticStyles.boostSectionNav}>
                            <span style={staticStyles.boostSectionTitle}>슬릭부스트</span>
                            <button style={staticStyles.boostSectionViewAllButton} onClick={handleSleekBoostClick}>
                                <span style={staticStyles.boostSectionViewAllText}>전체보기</span>
                                <div style={staticStyles.rightarrowDiv}>
                                    <img src={rightarrow} alt="->" style={staticStyles.boostSectionViewAllArrow} />
                                </div>
                            </button>
                        </div>
                        <div className='boost-coach-list' style={staticStyles.coachCardList}>
                            {boostCoaches.length > 0 ? boostCoaches.map((coach, index) => (
                                <BoostCoachCard
                                    key={`sb-${coach.sbKey || 'none'}-${index}`}
                                    coach={coach}
                                    index={index}
                                    boostTime={boostTime}
                                    inactiveTimeIds={inactiveTimeIds}
                                    onViewAll={handleViewAllClick}
                                    onTimeClick={handleTimeButtonClick}
                                    onCoachClick={handleBoostCoachClick}
                                />
                            )) :
                                <div style={{ color: '#9A9A9A', textAlign: 'center', padding: '20px', width: '100%' }}>
                                    예약 가능한 수업이 없습니다.</div>}
                        </div>
                    </div>
                )}

                {/* [추가] IPT 섹션 */}
                {availableStKinds.includes('G') && (
                    <div className='boost-section' style={staticStyles.boostSection}>
                        <div className='boost-section-nav' style={staticStyles.boostSectionNav}>
                            <span style={staticStyles.boostSectionTitle}>IPT</span>
                            <button style={staticStyles.boostSectionViewAllButton} onClick={handleIptViewAllClick}>
                                <span style={staticStyles.boostSectionViewAllText}>전체보기</span>
                                <div style={staticStyles.rightarrowDiv}>
                                    <img src={rightarrow} alt="->" style={staticStyles.boostSectionViewAllArrow} />
                                </div>
                            </button>
                        </div>
                        <div className='boost-coach-list' style={staticStyles.coachCardList}>
                            {iptCoaches.length > 0 ? iptCoaches.map((coach, index) => (
                                <BoostCoachCard
                                    key={`ipt-${coach.ctKey || 'none'}-${index}`}
                                    coach={coach}
                                    index={index}
                                    boostTime={[]} // 개별 시간 사용
                                    inactiveTimeIds={inactiveTimeIds}
                                    onViewAll={handleViewAllClick}
                                    onTimeClick={handleTimeButtonClick}
                                    onCoachClick={handleIptCoachClick}
                                />
                            )) : (
                                <div style={{ color: '#9A9A9A', textAlign: 'center', padding: '20px', width: '100%' }}>
                                    예약 가능한 수업이 없습니다.
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* [추가] LYLO 섹션 */}
                {availableStKinds.includes('F') && (
                    <div className='boost-section' style={staticStyles.boostSection}>
                        <div className='boost-section-nav' style={staticStyles.boostSectionNav}>
                            <span style={staticStyles.boostSectionTitle}>LYLO</span>
                            <button style={staticStyles.boostSectionViewAllButton} onClick={handleLyloViewAllClick}>
                                <span style={staticStyles.boostSectionViewAllText}>전체보기</span>
                                <div style={staticStyles.rightarrowDiv}>
                                    <img src={rightarrow} alt="->" style={staticStyles.boostSectionViewAllArrow} />
                                </div>
                            </button>
                        </div>
                        <div className='boost-coach-list' style={staticStyles.coachCardList}>
                            {lyloCoaches.length > 0 ? lyloCoaches.map((coach, index) => (
                                <BoostCoachCard
                                    key={`lylo-${coach.ctKey || 'none'}-${index}`}
                                    coach={coach}
                                    index={index}
                                    boostTime={[]} // 개별 시간 사용
                                    inactiveTimeIds={inactiveTimeIds}
                                    onViewAll={handleViewAllClick}
                                    onTimeClick={handleTimeButtonClick}
                                    onCoachClick={handleLyloCoachClick}
                                />
                            )) : (
                                <div style={{ color: '#9A9A9A', textAlign: 'center', padding: '20px', width: '100%' }}>
                                    예약 가능한 수업이 없습니다.
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* [추가] PT 트레이너 섹션 */}
                {availableStKinds.includes('C') && (
                    <div className='ot-section' style={staticStyles.otSection}>
                        <div className='boost-section-nav' style={staticStyles.boostSectionNav}>
                            <span style={staticStyles.boostSectionTitle}>PT</span>
                            <button style={staticStyles.boostSectionViewAllButton} onClick={handlePtViewAllClick}>
                                <span style={staticStyles.boostSectionViewAllText}>전체보기</span>
                                <div style={staticStyles.rightarrowDiv}>
                                    <img src={rightarrow} alt="->" style={staticStyles.boostSectionViewAllArrow} />
                                </div>
                            </button>
                        </div>
                        <div className='boost-coach-list' style={staticStyles.coachCardList}>
                            {/* 기존 PT 트레이너 카드 UI 주석 처리
                            {ptCoaches.length > 0 ? (
                                ptCoaches.map((trainer, index) => (
                                    <div
                                        key={`pt-${trainer.mnKey || trainer.id || 'none'}-${index}`}
                                        style={{ ...staticStyles.otCard, cursor: 'pointer' }}
                                        onClick={() => handlePtCoachClick(trainer)}
                                    >
                                        <div style={staticStyles.otCardImageContainer}>
                                            <img
                                                src={trainer.img || trainer.sbImgUrl || defaultCoachImg}
                                                alt={trainer.mnName || trainer.coachName}
                                                style={staticStyles.otCardImage}
                                            />
                                        </div>
                                        <div style={staticStyles.otCardInfo}>
                                            <span style={staticStyles.otCardName}>
                                                {trainer.mnName || trainer.coachName}
                                            </span>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div style={{ color: '#9A9A9A', textAlign: 'center', padding: '20px', width: '100%' }}>
                                    예약 가능한 PT 트레이너가 없습니다.
                                </div>
                            )}
                            */}
                            {ptCoaches.length > 0 ? (
                                ptCoaches.map((coach, index) => (
                                    <BoostCoachCard
                                        key={`pt-${coach.ctKey || 'none'}-${index}`}
                                        coach={coach}
                                        index={index}
                                        boostTime={[]} // 개별 시간 사용
                                        inactiveTimeIds={inactiveTimeIds}
                                        onViewAll={handleViewAllClick}
                                        onTimeClick={handleTimeButtonClick}
                                        onCoachClick={handlePtCoachClick}
                                    />
                                ))
                            ) : (
                                <div style={{ color: '#9A9A9A', textAlign: 'center', padding: '20px', width: '100%' }}>
                                    예약 가능한 수업이 없습니다.
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* [수정] OT 트레이너 섹션 (API 데이터 연동) */}
                {availableStKinds.includes('B') && (
                    <div className='ot-section' style={staticStyles.otSection}>
                        <p style={staticStyles.otSectionTitle}>무료 OT</p>
                        <div className='ot-coach-list' style={staticStyles.otCardList}>
                            {otCoaches.length > 0 ? (
                                otCoaches.map((trainer, index) => (
                                    <div
                                        key={`ot-${trainer.mnKey || trainer.id || 'none'}-${index}`}
                                        style={{ ...staticStyles.otCard, cursor: 'pointer' }}
                                    // onClick={() => handleOtCoachClick(trainer)} // 클릭 시 상세 이동
                                    >
                                        <div style={staticStyles.otCardImageContainer}>
                                            <img
                                                src={trainer.img || trainer.sbImgUrl || defaultCoachImg}
                                                alt={trainer.mnName || trainer.coachName}
                                                style={staticStyles.otCardImage}
                                            />
                                        </div>
                                        <div style={staticStyles.otCardInfo}>
                                            <span style={staticStyles.otCardName}>
                                                {trainer.mnName || trainer.coachName}
                                            </span>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div style={{ color: '#9A9A9A', textAlign: 'center', padding: '20px', width: '100%' }}>
                                    예약 가능한 OT 트레이너가 없습니다.
                                </div>
                            )}
                        </div>
                    </div>
                )}
                <Footer />
            </main>

            <QrModal show={showQR} onClose={closeQrModal} qrImage={qrData} />
            <NotYetModal isOpen={isNotYetModalOpen} onClose={handleCloseNotYetModal} message={notYetMessage} />
            {/* [추가] 회원정보 수정 모달 */}
            <ProfileUpdateModal
                isOpen={isProfileModalOpen}
                onClose={() => setIsProfileModalOpen(false)}
                onConfirm={handleProfileUpdateConfirm}
            />

            <BottomSheet
                show={showSheet}
                onClose={() => {
                    setShowSheet(false);
                    setInitialTimeData(null);
                }}
                onConfirm={handleSheetConfirm}
                initialData={initialTimeData}
                // availableTimes={selectedCoachInfo ? (boostCoaches.find(c => (c.sbName || c.coachName) === selectedCoachInfo.coachName)?.times || boostTime) : []}
                // API로 받아온 'sheetTimes'를 전달해야 합니다.
                availableTimes={sheetTimes}
                selectedDate={currentSelectedDate}
                brName={currentBranch?.brName}

                // 날짜 변경 시 API 호출 함수 전달
                onDateChange={handleDateChange}
            />

            <ConfirmModal isOpen={isConfirmModalOpen} onClose={handleCloseConfirmModal}
                onConfirm={handleFinalReservation} title="예약 확인"
                message={pendingReservation
                    ? `${pendingReservation.coachName}\n${pendingReservation.date} ${pendingReservation.time}\n\n선택하신 일시로 예약하시겠습니까?`
                    : ""}
            />

            {/* [추가] 공통 알림 모달 */}
            <ConfirmModal
                isOpen={alertModal.isOpen}
                onClose={closeAlertModal}
                onConfirm={closeAlertModal}
                title={alertModal.title}
                message={alertModal.message}
                showCancel={false}
            />
        </div>
    );
}

// ... (staticStyles는 기존과 동일하므로 생략) ...
const staticStyles = {
    homeBackgroundWrapper: {
        background: 'transparent', width: '100%', height: '100%', display: 'flex',
        flexDirection: 'column', alignItems: 'stretch', overflowY: 'auto',
    },
    fixedHeaderWrapper: {
        position: 'relative', top: 0, left: 0, width: '100%', zIndex: 100, backgroundColor: 'transparent',
    },
    statusBar: { flexShrink: 0, backgroundColor: 'transparent' },
    mainContent: {
        flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', alignItems: 'center',
        gap: '19px', scrollbarWidth: 'none', msOverflowStyle: 'none'
    },
    locationBarWrapper: { width: '100%', marginBottom: '9.5px', },
    userCardWrapper: {
        width: '100%', height: '167px', borderRadius: '10px',
        borderImageSource: 'linear-gradient(90deg, #8052FF 0%, #FFFFFF 50%, #0231FB 100%)',
        borderImageSlice: 1, padding: '1px', boxSizing: 'border-box', flexShrink: 0,
    },
    userCardInnerBasic: {
        width: '100%', height: '100%', boxSizing: 'border-box', display: 'flex', flexDirection: 'column',
        padding: '24px 20px', gap: '16px', backgroundColor: '#FCFCFA66', borderRadius: '9px', border: 'none'
    },
    userCardInnerPremium: {
        width: '100%',
        height: '100%',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        padding: '24px 20px',
        gap: '16px',
        background: 'linear-gradient(90deg, #8052FF 0%, #0231FB 100%)',
        borderRadius: '9px',
        border: 'none'
    },
    userCardText: { fontFamily: 'Pretendard', fontWeight: 500, fontSize: '14px', color: '#FCFCFA', margin: 0 },
    userCardQrButton: {
        width: '100%',
        height: '48px',
        borderRadius: '8px',
        background: '#FFFFFF4D',
        border: '1px solid #FCFCFA',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '10px'
    },
    userCardQrButtonText: { fontFamily: 'Pretendard', fontWeight: 700, fontSize: '14px', color: '#FCFCFA' },
    userCardQrIcon: { width: '18px', height: '18px' },
    userCardMembershipButton: {
        width: '100%',
        height: '48px',
        borderRadius: '8px',
        background: 'linear-gradient(90deg, #8052FF 0%, #0231FB 100%)',
        border: 'none',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '10px'
    },
    userCardMembershipButtonText: { fontFamily: 'Pretendard', fontWeight: 700, fontSize: '14px', color: '#FCFCFA' },
    userCardCrownIcon: { width: '20px', height: '18px' },
    userCardPremiumBadge: {
        backgroundColor: '#FCFCFA',
        borderRadius: '20px',
        padding: '4px 12px',
        border: '1px solid #8052FF',
        flexShrink: 0
    },
    userCardPremiumText: { fontFamily: 'Pretendard', fontWeight: 500, fontSize: '12px', color: '#8052FF' },
    userCardBasicBadge: { backgroundColor: '#9B77FF', borderRadius: '20px', padding: '4px 12px', flexShrink: 0 },
    userCardBasicText: { fontFamily: 'Pretendard', fontWeight: 500, fontSize: '12px', color: '#FFFFFF' },
    boostSection: { width: '100%', flexShrink: 0, paddingTop: '15px' },
    boostSectionNav: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        color: '#FCFCFA',
        width: '100%'
    },
    boostSectionTitle: { fontFamily: 'Pretendard', fontWeight: 600, fontSize: '20px' },
    // 전체보기 버튼 컨테이너 (Flex 적용)
    boostSectionViewAllButton: {
        background: 'none', border: 'none', padding: 0, margin: 0, font: 'inherit', color: 'inherit', cursor: 'pointer',
        textAlign: 'inherit', display: 'flex', alignItems: 'center', gap: '4px', textDecoration: 'none',
    },

    // 화살표 아이콘을 감싸는 div (필요 없다면 제거하고 img에 직접 스타일 적용 가능)
    rightarrowDiv: {
        width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center',
    },

    // 화살표 아이콘 이미지
    boostSectionViewAllArrow: { width: '100%', height: '60%', objectFit: 'contain', },
    coachCardList: {
        display: 'flex', overflowX: 'auto', gap: '12px', marginTop: '18px', scrollSnapType: 'x mandatory',
        scrollBehavior: 'smooth', scrollbarWidth: 'none', msOverflowStyle: 'none'
    },
    coachCard: { width: '236px', flexShrink: 0, color: '#FCFCFA', scrollSnapAlign: 'start' },
    coachCardImageContainer: {
        position: 'relative', width: '236px', height: '143px', borderRadius: '8px', overflow: 'hidden', backgroundColor: '#1B1931'
    },
    coachCardImage: { width: '100%', height: '100%', objectFit: 'contain', display: 'block' },
    coachCardFlagIcon: {
        position: 'absolute', top: '12px', right: '12px', width: '28px', height: '28px',
        background: '#21283666', borderRadius: '50%', boxSizing: 'border-box'
    },
    coachCardInfo: { color: '#FCFCFA', marginTop: '14px' },
    coachCardName: { fontFamily: 'Pretendard', fontWeight: 700, fontSize: '16px', color: '#FCFCFA' },
    coachCardStats: {
        display: 'flex', alignItems: 'center', gap: '4px', marginTop: '8px',
        color: '#E0E0E0', fontFamily: 'Pretendard', fontSize: '14px'
    },
    coachCardStarIcon: { width: '16px', height: '16px' },
    coachCardTimeContainer: {
        display: 'flex', gap: '4px', marginTop: '12px', flexWrap: 'nowrap', overflowX: 'auto', scrollbarWidth: 'none',
        msOverflowStyle: 'none'
    },
    coachCardTimeButtonActive: {
        width: 'auto', minWidth: '60px', flexShrink: 0, height: '34px', borderRadius: '6px', background: '#0D042D',
        color: '#FFFFFF', border: '1px solid #8052FF', padding: '0 12px', fontFamily: 'Pretendard', fontWeight: 500,
        fontSize: '12px', cursor: 'pointer', whiteSpace: 'nowrap'
    },
    coachCardTimeButtonInactive: {
        width: 'auto', minWidth: '60px', flexShrink: 0, height: '34px', borderRadius: '6px', background: '#333333',
        color: '#747474', border: '1px solid #555555', padding: '0 12px', fontFamily: 'Pretendard',
        fontWeight: 500, fontSize: '12px', cursor: 'pointer', whiteSpace: 'nowrap', opacity: 0.6
    },
    otSection: { width: '100%', flexShrink: 0, paddingTop: '15px', scrollSnapAlign: 'start', },
    otSectionTitle: {
        fontFamily: 'Pretendard', fontWeight: 600, fontSize: '20px', color: '#FCFCFA', margin: '0 0 18px 0'
    },
    otCardList: { display: 'flex', overflowX: 'auto', gap: '12px', scrollbarWidth: 'none', msOverflowStyle: 'none' },
    otCard: { width: '110px', flexShrink: 0, color: '#FCFCFA' },
    otCardImageContainer: { width: '110px', height: '110px', borderRadius: '8px', overflow: 'hidden' },
    otCardImage: { width: '100%', height: '100%', objectFit: 'cover', display: 'block' },
    otCardInfo: { marginTop: '12px' },
    otCardName: { fontFamily: 'Pretendard', fontWeight: 700, fontSize: '16px', color: '#FCFCFA' },
    retryButton: { padding: '10px 20px', background: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer' },
    testButton: {
        padding: '8px', cursor: 'pointer', width: '100%', flexShrink: 0, background: '#555', color: 'white',
        border: 'none', borderRadius: '4px'
    },
    userCardHeader: {
        width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0
    },
    userCardName: { fontFamily: 'Pretendard', fontWeight: 700, fontSize: '20px', color: '#FCFCFA' },// 모달 스타일
    modalOverlay: {
        position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
        backgroundColor: 'rgba(0, 0, 0, 0.7)', zIndex: 9999,
        display: 'flex', justifyContent: 'center', alignItems: 'center',
        padding: '24px'
    },
    modalContent: {
        width: '100%', maxWidth: '320px', backgroundColor: '#1B1931',
        borderRadius: '16px', padding: '24px', display: 'flex', flexDirection: 'column',
        alignItems: 'center', gap: '16px', boxShadow: '0 4px 20px rgba(0,0,0,0.5)'
    },
    modalTitle: {
        fontFamily: 'Pretendard', fontWeight: 700, fontSize: '18px', color: '#FCFCFA', margin: 0
    },
    modalDesc: {
        fontFamily: 'Pretendard', fontWeight: 400, fontSize: '14px', color: '#9A9A9A',
        textAlign: 'center', lineHeight: '1.5', margin: 0
    },
    inputGroup: {
        width: '100%', display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '8px'
    },
    modalInput: {
        width: '100%', height: '48px', backgroundColor: '#2C2A4A', border: '1px solid #3A394A',
        borderRadius: '8px', padding: '0 16px', color: '#FCFCFA', fontSize: '14px',
        fontFamily: 'Pretendard', outline: 'none', boxSizing: 'border-box'
    },
    modalButtonData: {
        width: '100%', display: 'flex', gap: '10px', marginTop: '8px'
    },
    modalConfirmButton: {
        flex: 1, height: '44px', borderRadius: '8px', border: 'none',
        background: 'linear-gradient(90deg, #8052FF 0%, #0231FB 100%)',
        color: 'white', fontWeight: 600, fontSize: '14px', cursor: 'pointer'
    },
    modalCloseButton: {
        flex: 1, height: '44px', borderRadius: '8px', border: '1px solid #3A394A',
        background: 'transparent', color: '#9A9A9A', fontWeight: 500,
        fontSize: '14px', cursor: 'pointer'
    }
};

export default Home;